// Live RF learning — what the receiver has just heard, on screen, now.
//
// The receiver announces every code it decodes as
// `[<uno>]RfV(<decimal>,<binary>)` on `<uno>/client`, so an installer can press
// the sensor and see the code appear instead of typing it.
//
// ⚠️ LISTENING IS ALL THIS DOES. The same press is also POSTed to the server by
// the receiver, and the server publishes the mapped actions. If the app acted
// on `RfV` as well, every RF-driven switch would fire twice.
//
// ⚠️ ONE EVENT, NOT ONE FRAME. A transmitter repeats a frame 4-8 times per
// press, so the same code arrives in a burst; a list that showed each frame
// would scroll six identical rows per door opening. Repeats inside
// RF_REPEAT_MS only bump a counter on the row that is already there — which is
// also what the backend does before it logs or notifies.

import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';
import { parseRfV, transmitterOf, eventByteOf } from '../devices/rfCodes';

export const RF_REPEAT_MS = 3000;
const MAX_HEARD = 30;

/**
 * @param receiverUno  the RF receiver to listen to, or '' for every receiver
 *                     (the admin page, where the receiver is not known yet).
 * -> { heard, clear, listening }
 *    heard: [{ code, binary, transmitterId, eventByte, at, repeats }], newest first
 */
export default function useRfLearn(receiverUno) {
  const [heard, setHeard] = useState([]);
  const [listening, setListening] = useState(false);
  // The list is also read inside the listener, which must not re-subscribe on
  // every message — so the de-duplication works off a ref, not state.
  const seen = useRef(new Map());   // code -> last ms

  const clear = useCallback(() => { seen.current = new Map(); setHeard([]); }, []);

  useEffect(() => {
    const topic = receiverUno ? `${receiverUno}/client` : '#';
    subscribeTopic(topic);
    setListening(true);
    const off = addMessageListener((t, payload) => {
      if (receiverUno && t !== topic) return;
      const hit = parseRfV(payload);
      if (!hit) return;
      const now = Date.now();
      const prev = seen.current.get(hit.code);
      seen.current.set(hit.code, now);
      const from = String(t || '').split('/')[0];
      setHeard((list) => {
        if (prev != null && now - prev < RF_REPEAT_MS) {
          // Same burst: count it, do not add a row.
          return list.map((r) => (r.code === hit.code
            ? { ...r, repeats: r.repeats + 1, at: now } : r));
        }
        const row = {
          code: hit.code,
          binary: hit.binary,
          transmitterId: transmitterOf(hit.code),
          eventByte: eventByteOf(hit.code),
          receiverUno: from,
          at: now,
          repeats: 1,
        };
        return [row, ...list.filter((r) => r.code !== hit.code)].slice(0, MAX_HEARD);
      });
    });
    return () => {
      off();
      unsubscribeTopic(topic);
      setListening(false);
    };
  }, [receiverUno]);

  return { heard, clear, listening };
}
